import { ElementRef, Injectable } from '@angular/core';
import { initCssomCacheAsync, PrintCssomData } from './widget-print-resize';

interface Subscriber {
    getElement: () => HTMLElement | null;
    resize: (printWidthPx?: number) => void;
}

interface PrintTask {
    token: symbol;
    sub: Subscriber;
    element: HTMLElement;
}

interface PrintRowTasks {
    printWidthPx: number;
    tasks: PrintTask[];
}

interface OriginalWidth {
    value: string;
    priority: string;
}

const MAX_STABILIZATION_ITERATIONS = 15;
const REQUIRED_STABLE_ITERATIONS = 3;
const LAYOUT_TOLERANCE_PX = 0.1;

@Injectable()
export class WidgetPrintResizeCoordinatorService {

    private _activationCount = 0;
    private _printActive = false;
    private _subscribers = new Map<symbol, Subscriber>();
    private _cssomCache: PrintCssomData | null = null;
    private _printTokens = new Set<symbol>();
    // Capture each target row once: several widgets can live in the same components-row.
    private _targetOrigWidths = new Map<HTMLElement, OriginalWidth>();
    private _printBootstrapGridStyleEl: HTMLStyleElement | null = null;
    private _printTabOverflowStyleEl: HTMLStyleElement | null = null;
    private _printLandscapeWidthStyleEl: HTMLStyleElement | null = null;

    private readonly _beforePrintHandler = () => this.onBeforePrint();
    private readonly _afterPrintHandler = () => this.onAfterPrint();

    activate(): () => void {
        this._activationCount++;
        if (this._activationCount === 1) {
            this.start();
        }
        let released = false;
        return () => {
            if (released) return;
            released = true;
            this._activationCount--;
            if (this._activationCount === 0) {
                this.stop();
            }
        };
    }

    subscribe(
        getElement: () => HTMLElement | null,
        resize: (printWidthPx?: number) => void
    ): () => void {
        const token = Symbol('widget-print-resize');
        this._subscribers.set(token, { getElement, resize });
        let unsubscribed = false;
        return () => {
            if (unsubscribed) return;
            unsubscribed = true;
            this._subscribers.delete(token);
            this._printTokens.delete(token);
        };
    }

    subscribeElement(
        elementRef: ElementRef,
        resize: (printWidthPx?: number) => void = () => { }
    ): () => void {
        return this.subscribe(() => elementRef.nativeElement, resize);
    }

    private start(): void {
        this.injectBootstrapGridStyle();
        this.injectTabOverflowStyle();
        initCssomCacheAsync().then(data => {
            if (this._activationCount > 0) {
                this._cssomCache = data;
                this.injectLandscapeWidthStyle(data.landscapeWidthPx);
            }
        });
        window.addEventListener('beforeprint', this._beforePrintHandler);
        window.addEventListener('afterprint', this._afterPrintHandler);
    }

    private stop(): void {
        window.removeEventListener('beforeprint', this._beforePrintHandler);
        window.removeEventListener('afterprint', this._afterPrintHandler);
        this.restoreAllStyles();
        this._printActive = false;
        this._cssomCache = null;
        this.removeBootstrapGridStyle();
        this.removeTabOverflowStyle();
        this.removeLandscapeWidthStyle();
    }

    private injectBootstrapGridStyle(): void {
        if (this._printBootstrapGridStyleEl) return;
        // Bootstrap's col-md-* rules live inside @media (min-width: 768px). During print,
        // Chrome evaluates that query against the print page width (not the container width).
        // If the page width after margins falls below 768px the rules are removed and all
        // col-md-* columns collapse to 100% width, breaking any internal two-column layout.
        // Re-declaring the same percentages inside @media print bypasses the viewport check:
        // the columns always keep their proportions, and the percentages resolve correctly
        // because their parent container has already been frozen to a pixel width by the
        // coordinator before the print snapshot is taken.
        const cols = [8.333333, 16.666667, 25, 33.333333, 41.666667, 50, 58.333333, 66.666667, 75, 83.333333, 91.666667, 100];
        const css = cols.map((p, i) => `.col-md-${i + 1}{flex:0 0 ${p}%!important;max-width:${p}%!important}`).join('');
        const style = document.createElement('style');
        style.setAttribute('data-widget-print-bootstrap-grid', '');
        style.textContent = `@media print{${css}}`;
        document.head.appendChild(style);
        this._printBootstrapGridStyleEl = style;
    }

    private removeBootstrapGridStyle(): void {
        this._printBootstrapGridStyleEl?.remove();
        this._printBootstrapGridStyleEl = null;
    }

    // Injected after Angular Material's dynamically-scoped styles so the cascade override wins.
    private injectTabOverflowStyle(): void {
        if (this._printTabOverflowStyleEl) return;
        const style = document.createElement('style');
        style.setAttribute('data-widget-print-tab-overflow', '');
        style.textContent = '@media print{.mat-tab-group.servitly-tab-group .mat-tab-body-content,.mat-tab-body,.mat-tab-body-wrapper{overflow:visible !important;}}';
        document.head.appendChild(style);
        this._printTabOverflowStyleEl = style;
    }

    private removeTabOverflowStyle(): void {
        this._printTabOverflowStyleEl?.remove();
        this._printTabOverflowStyleEl = null;
    }

    private injectLandscapeWidthStyle(widthPx: number): void {
        if (this._printLandscapeWidthStyleEl) return;
        const style = document.createElement('style');
        style.setAttribute('data-widget-print-landscape-width', '');
        style.textContent = `@media print{.landscape-page{width:${widthPx}px !important;}}`;
        document.head.appendChild(style);
        this._printLandscapeWidthStyleEl = style;
    }

    private removeLandscapeWidthStyle(): void {
        this._printLandscapeWidthStyleEl?.remove();
        this._printLandscapeWidthStyleEl = null;
    }

    private findResizeTarget(element: HTMLElement): HTMLElement | null {
        let el: HTMLElement | null = element.parentElement;
        while (el && el !== document.body) {
            if (el.classList.contains('components-row')) return el;
            el = el.parentElement;
        }
        return null;
    }

    private onBeforePrint(): void {
        if (this._printActive) return;
        this._printActive = true;

        // Phase 1: Collect tasks; skip subscribers whose CSSOM data is unavailable.
        const rowTasks = new Map<HTMLElement, PrintRowTasks>();
        this._subscribers.forEach((sub, token) => {
            try {
                const element = sub.getElement();
                if (!element || !element.isConnected) return;
                const row = this.findResizeTarget(element);
                if (!row) return;
                if (this._cssomCache) {
                    const isLandscape = row.classList.contains('landscape-page');
                    const printWidthPx = isLandscape ? this._cssomCache.landscapeWidthPx : this._cssomCache.portraitWidthPx;
                    let group = rowTasks.get(row);
                    if (!group) {
                        group = { printWidthPx, tasks: [] };
                        rowTasks.set(row, group);
                    }
                    group.tasks.push({ token, sub, element });
                } else {
                    try { sub.resize(); } catch (err) { /* widget resize failed; ignore */ }
                }
            } catch (err) {
                /* skip this subscriber */
            }
        });

        if (rowTasks.size === 0) return;

        // Phase 2: Constrain each target row to its print width.
        rowTasks.forEach((group, row) => {
            if (!this._targetOrigWidths.has(row)) {
                this._targetOrigWidths.set(row, {
                    value: row.style.getPropertyValue('width'),
                    priority: row.style.getPropertyPriority('width')
                });
                row.style.setProperty('width', `${group.printWidthPx}px`, 'important');
            }
        });

        let stableIterationCount = 0;
        for (let iteration = 0; iteration < MAX_STABILIZATION_ITERATIONS; iteration++) {
            const beforeResize = this.measureLayout(rowTasks);

            rowTasks.forEach(group => {
                group.tasks.forEach(({ token, sub }) => {
                    try {
                        this._printTokens.add(token);
                        sub.resize();
                    } catch (err) {
                        /* skip this subscriber */
                    }
                });
            });

            const afterResize = this.measureLayout(rowTasks);
            if (this.isSameLayout(beforeResize, afterResize)) {
                stableIterationCount++;
                if (stableIterationCount >= REQUIRED_STABLE_ITERATIONS) {
                    break;
                }
            } else {
                stableIterationCount = 0;
            }
        }
    }

    private measureLayout(rowTasks: Map<HTMLElement, PrintRowTasks>): number[] {
        const snapshot: number[] = [];

        rowTasks.forEach((group, row) => {
            const rowRect = row.getBoundingClientRect();
            const children = Array.from(row.children) as HTMLElement[];
            const directChildren = new Set<HTMLElement>(children);
            const nestedHosts: HTMLElement[] = [];
            const measuredHosts = new Set<HTMLElement>();

            snapshot.push(rowRect.width, rowRect.height, children.length);
            children.forEach(child => {
                const childRect = child.getBoundingClientRect();
                snapshot.push(
                    childRect.left - rowRect.left,
                    childRect.top - rowRect.top,
                    childRect.width,
                    childRect.height
                );
            });

            group.tasks.forEach(({ element }) => {
                if (!directChildren.has(element) && !measuredHosts.has(element)) {
                    measuredHosts.add(element);
                    nestedHosts.push(element);
                }
            });

            snapshot.push(nestedHosts.length);
            nestedHosts.forEach(host => {
                const hostRect = host.getBoundingClientRect();
                snapshot.push(hostRect.width, hostRect.height);
            });
        });

        return snapshot;
    }

    private isSameLayout(previous: number[], current: number[]): boolean {
        if (previous.length !== current.length) return false;
        return previous.every((value, index) => Math.abs(value - current[index]) <= LAYOUT_TOLERANCE_PX);
    }

    private onAfterPrint(): void {
        this.restoreAllStyles();
        this._printActive = false;
    }

    private restoreAllStyles(): void {
        const restoredRows = Array.from(this._targetOrigWidths.keys());
        this._targetOrigWidths.forEach((originalWidth, row) => {
            if (originalWidth.value) {
                row.style.setProperty('width', originalWidth.value, originalWidth.priority);
            } else {
                row.style.removeProperty('width');
            }
        });
        this._targetOrigWidths.clear();

        restoredRows.forEach(row => void row.getBoundingClientRect());

        this._printTokens.forEach(token => {
            const sub = this._subscribers.get(token);
            if (!sub) return;
            try { sub.resize(); } catch (err) { /* ignore */ }
        });
        this._printTokens.clear();
    }
}
